import { Box, Group, Modal, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import type { EmbeddedPromptOut, Message } from '@repo/api-codegen';
import { useApiListAiQueries, useApiUpdateAiQuery } from '@repo/api-codegen';
import { CopyButton, IconButton, Text } from '@repo/foundations';
import { memo, useCallback, useMemo } from 'react';
import { useStreamingContent } from '../../../hooks/useStreamingContent';
import { useFeatureFlags } from '../../../utils/featureFlags';
import { AskQuestionButton } from './AskQuestionButton';
import { MessageAIError } from './MessageAIError';
import { MessageAILoading } from './MessageAILoading';
import { MessageContentV3 } from './MessageContentV3';
import { MessageToolbar } from './MessageToolbar';
import { NegativeFeedbackButton } from './NegativeFeedbackButton';
import { PositiveFeedbackButton } from './PositiveFeedbackButton';

export interface AiMessageProps {
	message: EmbeddedPromptOut;
	parentPrompt?: EmbeddedPromptOut;
	onRetry?: (messageId: string) => void;
	isLastMessage: boolean;
}

export const AiMessage = memo(
	({ message, parentPrompt, onRetry, isLastMessage }: AiMessageProps) => {
		const [opened, { open, close }] = useDisclosure(false);

		const messageContent = useStreamingContent(
			message.status === 'running',
			message.response?.content ?? ''
		);

		const messages = useMemo(
			() =>
				message.structured_response?.length
					? message.structured_response
					: [
							{
								content: messageContent,
								sender: 'assistant',
								use_tools: [],
								tool_response: null,
							} as Message,
						],
			[message.structured_response, messageContent]
		);

		const handleOnRetry = useCallback(() => {
			onRetry?.(message.id);
		}, [onRetry, message.id]);

		const shouldDisplayLoading =
			message.status === 'pending' ||
			(message.status === 'running' &&
				messageContent.length === 0 &&
				!message.structured_response?.length);
		const shouldDisplayFailure = message.status === 'failed';
		const shouldDisplayAnswerToolbar = message.status === 'completed';

		const { verifiableQueries } = useFeatureFlags();
		const { mutateAsync: mutateQuery } = useApiUpdateAiQuery({});
		const { data: response } = useApiListAiQueries({});

		const aiQuery = useCallback(
			async (id: string, prevVerified: boolean) => {
				try {
					const sql = 'sql through AI';
					const comment = 'reason';

					await mutateQuery({
						body: {
							id,
							sql,
							comment,
							verified: !prevVerified,
						},
					});
				} catch (error) {
					showNotification({
						message: 'Failed to create AI query',
						color: 'red',
					});
				}
			},
			[mutateQuery]
		);

		return (
			<MessageToolbar.Wrapper forceShow={isLastMessage}>
				{shouldDisplayLoading && (
					<MessageAILoading steps={message.response?.steps ?? []} />
				)}
				{shouldDisplayFailure && <MessageAIError onRetry={handleOnRetry} />}
				{!shouldDisplayLoading && !shouldDisplayFailure && (
					<Box>
						{['running', 'completed', 'stopped'].includes(
							message.status ?? ''
						) && (
							<MessageContentV3
								messages={messages}
								isRunning={message.status === 'running'}
							/>
						)}
						{shouldDisplayAnswerToolbar && (
							<MessageToolbar>
								<Group spacing="xs">
									<AskQuestionButton
										message={message}
										parentPrompt={parentPrompt}
										source="response"
										variant="default"
									/>
									<Group spacing="3xs">
										<CopyButton value={message.response?.content || ''} />
										<PositiveFeedbackButton message={message} />
										<NegativeFeedbackButton message={message} />
										{verifiableQueries && (
											// This button is to test the backend AI Query Suggestions
											// TODO: delete this button once the feature is ready
											// and a new page is created for the users to verify AI suggested queries
											<IconButton
												iconName="edit"
												variant="tertiary"
												onClick={() => {
													open();
												}}
											/>
										)}
									</Group>
								</Group>
							</MessageToolbar>
						)}

						<Modal
							opened={verifiableQueries && opened}
							onClose={close}
							title="AI Query"
						>
							{/* This is a rough FE feature to test the backend AI Query Suggestions */}
							{/* TODO: this will be moved to a new page */}
							{response?.map((query) => (
								<UnstyledButton
									key={query.id}
									onClick={() => {
										aiQuery(query.id, query.verified);
									}}
								>
									<Text size="sm">ID: {query.id}</Text>
									<Text size="xs">SQL: {query.sql}</Text>
									<Text size="xs">Comment: {query.comment}</Text>
									<Text size="xs">
										Verified: {query.verified ? 'Verified' : 'Unverified'}
									</Text>
									<Text size="xs">Verified by: {query.verified_by_id}</Text>
									<Text size="xs">Verified at: {query.verified_at}</Text>
									<Text>--</Text>
								</UnstyledButton>
							))}
						</Modal>
					</Box>
				)}
			</MessageToolbar.Wrapper>
		);
	}
);
